import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, homeFor } from "../lib/auth";
import { errMsg } from "../lib/api";

export function AuthCallback() {
  const { google } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    google(sid).then((u) => nav(homeFor(u), { replace: true })).catch((e) => { toast.error(errMsg(e)); nav("/login", { replace: true }); });
  }, [google, nav]);
  return <div className="min-h-screen grid place-items-center overline" data-testid="auth-callback">Signing you in…</div>;
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav(homeFor(user), { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { nav(homeFor(await login(email, password))); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-slate-950 lg:block">
        <img src="/images/walkthrough.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="grid-bg-dark absolute inset-0" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" data-testid="login-home-link"><Logo dark /></Link>
          <div>
            <div className="overline mb-4 text-teal-300">Inspection = Report Creation</div>
            <p className="font-display text-4xl font-extrabold leading-tight text-white">Every observation captured with what, where, photo, severity and recommendation.</p>
            <p className="mt-6 font-mono text-xs text-slate-400">PC-BLR-261006-001 · Report delivered on-site</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-10 lg:hidden"><Link to="/"><Logo /></Link></div>
          <div className="overline mb-2">Secure sign in</div>
          <h1 className="font-display text-3xl font-extrabold">Welcome back</h1>
          <p className="mt-2 text-sm text-slate-500">Admins, inspectors, builders and customers sign in here.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12" data-testid="login-email-input" /></div>
            <div><Label htmlFor="password">Password</Label><Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" data-testid="login-password-input" /></div>
            <button disabled={busy} className="tap flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800" data-testid="login-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </button>
          </form>
          <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />OR<div className="h-px flex-1 bg-slate-200" /></div>
          <button onClick={googleLogin} className="tap flex h-12 w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white font-semibold hover:bg-slate-50" data-testid="login-google-button">
            <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <p className="mt-2 text-center text-[11px] text-slate-400">Google sign-in works for accounts registered by your admin.</p>
        </div>
      </div>
    </div>
  );
}
